/**
 * GLOBAL-DP — Unified period + comparison-basis resolver (ZAR-1639).
 *
 * ONE place that answers, for any report date selection:
 *   • which calendar days the selection covers        (startDate / endDate)
 *   • which "shape" it is                             (single day, 7-day, calendar month, YTD, …)
 *   • which days it is measured against               (comparisonStart / comparisonEnd)
 *   • what to print in the date control               (selectionLabel / comparisonLabel)
 *   • whether the comparison is hour-truncated        (ZAR-1662)
 *
 * Per ZAR-1661 no component may compute its own comparison period — everything reads
 * `resolvePeriod()`.
 *
 * ── Token model ──────────────────────────────────────────────────────────────
 * The stored filter value stays the SAME string vocabulary the portal already
 * persists in `redux/reportFilters` ('Today' | 'Yesterday' | '7D' | 'This Month' |
 * 'This Year' | 'Last Month' | 'Last 3 Months' | 'Last 6 Months' | 'Last Year' |
 * 'custom:YYYY-MM-DD:YYYY-MM-DD'). Only the *rendered* label changes ("Last 7 days"),
 * so the ~200 existing `activeDate === '7D'` branches across the report screens keep
 * working untouched. Period steppers always emit an explicit `custom:from:to`.
 *
 * ── Anchoring ────────────────────────────────────────────────────────────────
 * Relative tokens resolve through the existing 5 AM store-timezone anchor helpers
 * (see getAnchorDate / DateProdIssue.md); this module must never re-derive them.
 *
 * ── Comparison availability is a property of the SHAPE, not of how it was made ─
 * A range typed into the calendar, chosen from a quick-select, or produced by a
 * stepper are all evaluated identically.
 */
import {
  dateStrInZone,
  shiftDateStr,
  hourInZone,
  getAnchorDate,
  getServerClock,
  currentStoreDate,
  getStoreTimeZone,
  getYesterdayRange,
  get7DayRange,
  get30DayRange,
  get90DayRange,
  getThisMonthRange,
  getThisYearRange,
} from './dateTimeUtils';

// ── Types ────────────────────────────────────────────────────────────────────

export type PeriodShape =
  | 'single-day'
  | 'seven-day'
  | 'thirty-day'
  /** Guest Reviews' 90-day window (`#UD 12`) — same floating-window rule as its siblings. */
  | 'ninety-day'
  | 'calendar-month'
  | 'three-calendar-months'
  | 'six-calendar-months'
  | 'calendar-year'
  | 'month-to-date'
  | 'year-to-date'
  /**
   * No date bound at all — the Guest Reviews "All" quick-select (`#UD 12`).
   *
   * NOT a range, and deliberately not expressible as one. A selection with no start
   * has no length, so it has no preceding window to compare against and nothing for
   * the period steppers to move by. Do not fake it with a wide `custom:` token: that
   * classifies as a real shape and invents a comparison period for a selection that
   * has none.
   */
  | 'unbounded'
  | 'unregistered';

export interface PeriodContext {
  /** Calendar date at the store right now, YYYY-MM-DD. */
  todayISO: string;
  /** Last finalised day under the 5 AM cutoff, YYYY-MM-DD. */
  anchorISO: string;
  /** Clock hour 0–23 at the store right now — the most recently completed hour. */
  nowHour: number;
}

export interface ResolvedPeriod {
  /** The token this was resolved from. */
  token: string;
  startDate: string;
  endDate: string;
  shape: PeriodShape;
  /** True when the selection is exactly the store's current calendar day. */
  isToday: boolean;
  /** First line of the collapsed control, e.g. "Today", "Thu, Jul 30", "Jul 25 – Jul 31". */
  selectionLabel: string;
  /** Second line, e.g. "vs Thu, Jul 23" or "No comparison for this range". */
  comparisonLabel: string;
  comparisonAvailable: boolean;
  /** '' when no comparison is available. */
  comparisonStart: string;
  comparisonEnd: string;
  /** ZAR-1662 — true only for a current-day selection. */
  hourTruncated: boolean;
  /** Hour 0–23 both windows are truncated to; null when not truncated. */
  truncationHour: number | null;
}

export const NO_COMPARISON_LABEL = 'No comparison for this range';

/**
 * Which window a report's KPI deltas are measured against — and therefore what the date
 * control's second line must name (`DPFIX-13`). The label is a promise about the
 * numbers under it, so it follows whoever computes those numbers.
 *
 * - `'shape'` (default) — the shape registry: same weekday a week back for one day, the
 *   prior month for a calendar month, month-to-date against the prior month's same days,
 *   Today truncated to the last completed hour. Snapshot, Sales and Labor SEND this window
 *   to their comparison endpoints (ZAR-1661), so label and numbers agree by construction.
 * - `'preceding'` — the equal-length window ending the day before the selection starts,
 *   for EVERY range, with no hour truncation. The Customers Report backend computes
 *   exactly that on its own (CUST-10.1; `resolveComparisonRange` in maghil-reports-be
 *   `application/customer-reports/business-date.util.ts`) and takes no comparison
 *   parameters, so here the label has to follow the backend rather than lead it.
 */
export type ComparisonBasis = 'shape' | 'preceding';

// ── Pure calendar helpers (UTC-noon anchored: immune to DST/offset flips) ─────

const pad2 = (n: number) => String(n).padStart(2, '0');
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const isIso = (s: unknown): s is string => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

/** [year, month1..12, day] of an ISO date string. */
const parts = (iso: string): [number, number, number] => {
  const [y, m, d] = iso.split('-').map(Number);
  return [y, m, d];
};

const utcNoon = (iso: string): Date => new Date(`${iso}T12:00:00Z`);

/** Days in a 1-indexed month. */
export const daysInMonth = (year: number, month1: number): number =>
  new Date(Date.UTC(year, month1, 0)).getUTCDate();

/** Inclusive day count between two ISO dates (same day = 1). */
export const spanDays = (from: string, to: string): number =>
  Math.round((utcNoon(to).getTime() - utcNoon(from).getTime()) / 86400000) + 1;

/** Weekday index 0–6 (Sun–Sat) of an ISO date. */
export const weekdayOf = (iso: string): number => utcNoon(iso).getUTCDay();

const firstOfMonth = (year: number, month1: number): string => `${year}-${pad2(month1)}-01`;
const lastOfMonth = (year: number, month1: number): string =>
  `${year}-${pad2(month1)}-${pad2(daysInMonth(year, month1))}`;

const isFirstOfMonth = (iso: string): boolean => parts(iso)[2] === 1;
const isLastOfMonth = (iso: string): boolean => {
  const [y, m, d] = parts(iso);
  return d === daysInMonth(y, m);
};

/** Shift a 1-indexed (year, month) pair by whole months. */
const shiftMonth = (year: number, month1: number, delta: number): [number, number] => {
  const idx = year * 12 + (month1 - 1) + delta;
  return [Math.floor(idx / 12), (idx % 12) + 1];
};

/** Whole calendar months from `from`'s month to `to`'s month, inclusive (same month = 1). */
const monthSpan = (from: string, to: string): number => {
  const [fy, fm] = parts(from);
  const [ty, tm] = parts(to);
  return (ty * 12 + tm) - (fy * 12 + fm) + 1;
};

/**
 * Same day-of-month in a shifted month, clamped to that month's length — so
 * "Mar 31 one month earlier" is Feb 28/29, not an overflow into March.
 */
const sameDayInShiftedMonth = (iso: string, monthDelta: number): string => {
  const [y, m, d] = parts(iso);
  const [ny, nm] = shiftMonth(y, m, monthDelta);
  return `${ny}-${pad2(nm)}-${pad2(Math.min(d, daysInMonth(ny, nm)))}`;
};

/** Same month/day in a shifted year, clamping Feb 29 → Feb 28 in a common year. */
const sameDayInShiftedYear = (iso: string, yearDelta: number): string => {
  const [y, m, d] = parts(iso);
  const ny = y + yearDelta;
  return `${ny}-${pad2(m)}-${pad2(Math.min(d, daysInMonth(ny, m)))}`;
};

// ── Token → { startDate, endDate } ───────────────────────────────────────────

/** Parse a `custom:from:to` token. Returns null for anything else or a malformed value. */
export const parseCustomToken = (token: unknown): { startDate: string; endDate: string } | null => {
  if (typeof token !== 'string' || !token.startsWith('custom:')) return null;
  const p = token.split(':');
  if (p.length !== 3 || !isIso(p[1]) || !isIso(p[2]) || p[1] > p[2]) return null;
  return { startDate: p[1], endDate: p[2] };
};

/** Build the explicit-range token a stepper / calendar Apply emits. */
export const customToken = (startDate: string, endDate: string): string => `custom:${startDate}:${endDate}`;

/** Normalised comparison key for a preset token ("This Month" → "thismonth"). */
export const normalizeToken = (token: unknown): string =>
  (typeof token === 'string' ? token : '').trim().toLowerCase().replace(/\s+/g, '');

/**
 * Token → calendar range. Supersedes the four duplicated `getStartEndFromFilter`
 * copies (SnapshotScreen, SalesScreen, reportsApi, useEodSummaryData) — every preset
 * from every report resolves here, so a range carried across reports by the shared
 * filter never lands on a screen that cannot resolve it.
 *
 * Returns null only for an unrecognised token.
 */
export function resolveTokenRange(
  token: string,
  timeZoneCd: string = getStoreTimeZone()
): { startDate: string; endDate: string } | null {
  const custom = parseCustomToken(token);
  if (custom) return custom;

  const key = normalizeToken(token);
  // The live day comes from the server's clock when one has arrived, so the range behind
  // "Today" is the STORE's day rather than the device's reading of it — and matches the
  // date printed in the control. Everything below still anchors on `getAnchorDate`.
  const todayISO = currentStoreDate(timeZoneCd);

  if (key === 'today') return { startDate: todayISO, endDate: todayISO };
  if (key === 'yesterday') return getYesterdayRange(timeZoneCd);
  if (key === '7d' || key === 'last7days') return get7DayRange(timeZoneCd);
  if (key === '30d' || key === 'last30days') return get30DayRange(timeZoneCd);
  // Guest Reviews' long window (`#UD 12`). Anchored exactly like its 7D/30D siblings —
  // a preset that ended on a different day from the ones beside it in the same
  // quick-select row would read as a bug, not as a feature.
  if (key === '90d' || key === 'last90days') return get90DayRange(timeZoneCd);
  if (key === 'thismonth') return getThisMonthRange(timeZoneCd);
  // "This Year" is a calendar range like "This Month", not a relative one — it names
  // the year the store is currently trading in, so it must always run through today
  // rather than the last finalised (5 AM-anchored) day. DPFIX-10; see
  // ThisYearDateProdIssue/ThisYearDateProdIssueBugFix.md.
  if (key === 'thisyear') return getThisYearRange(timeZoneCd);

  // Long-range presets are month/year aligned off the anchor date, so they stay
  // correct when the 5 AM cutoff rolls the anchor back into the previous month.
  const anchor = getAnchorDate(timeZoneCd);
  const [ay, am] = parts(anchor);

  if (key === 'lastmonth') {
    const [y, m] = shiftMonth(ay, am, -1);
    return { startDate: firstOfMonth(y, m), endDate: lastOfMonth(y, m) };
  }
  if (key === 'last3months' || key === 'last6months') {
    const n = key === 'last3months' ? 3 : 6;
    const [ey, em] = shiftMonth(ay, am, -1); // ends with the last complete month
    const [sy, sm] = shiftMonth(ey, em, -(n - 1));
    return { startDate: firstOfMonth(sy, sm), endDate: lastOfMonth(ey, em) };
  }
  if (key === 'lastyear') return { startDate: `${ay - 1}-01-01`, endDate: `${ay - 1}-12-31` };

  return null;
}

// ── Shape registry (ZAR-1661) ────────────────────────────────────────────────

/**
 * Classify a range. Order encodes the precedence the spec calls for:
 *
 *  • single-day wins outright — "This month" on the 1st of the month is a single day,
 *    so it compares week-over-week, not to a one-day window in the prior month.
 *  • calendar-aligned shapes beat the raw 30-day shape — Apr 1–30 is a calendar month
 *    (compares to full March), while a floating 30-day window compares to the 30 days
 *    before it. A 31-day range that is NOT a calendar month matches nothing.
 *  • to-date shapes only apply while the range is still running (ends today/anchor).
 */
export function classifyShape(startDate: string, endDate: string, ctx: PeriodContext): PeriodShape {
  if (!isIso(startDate) || !isIso(endDate) || startDate > endDate) return 'unregistered';

  if (startDate === endDate) return 'single-day';

  const [sy, sm] = parts(startDate);
  const [ey, em] = parts(endDate);
  const startsMonth = isFirstOfMonth(startDate);
  const endsMonth = isLastOfMonth(endDate);
  // "Still running" — the trailing edge is the live day (today) or the last finalised
  // day (anchor), which is what the relative presets actually resolve to.
  const isRunning = endDate === ctx.todayISO || endDate === ctx.anchorISO;

  // Complete calendar year: Jan 1 → Dec 31 of the same year.
  if (startsMonth && sm === 1 && endsMonth && em === 12 && sy === ey) return 'calendar-year';

  // Complete calendar months (1, 3 or 6) — checked before the to-date shapes so a
  // month that has just finished compares to the whole prior month.
  if (startsMonth && endsMonth) {
    const months = monthSpan(startDate, endDate);
    if (months === 1) return 'calendar-month';
    if (months === 3) return 'three-calendar-months';
    if (months === 6) return 'six-calendar-months';
  }

  // Year to date: Jan 1 → the running edge.
  if (startsMonth && sm === 1 && sy === ey && isRunning) return 'year-to-date';

  // Month to date: 1st of the month → the running edge.
  if (startsMonth && sy === ey && sm === em && isRunning) return 'month-to-date';

  const span = spanDays(startDate, endDate);
  if (span === 7) return 'seven-day';
  if (span === 30) return 'thirty-day';
  if (span === 90) return 'ninety-day';

  return 'unregistered';
}

/**
 * Comparison window for a classified range. Returns null when the shape carries no
 * comparison — the caller renders "No comparison for this range" and every KPI delta
 * falls back to an em dash.
 */
export function resolveComparisonRange(
  startDate: string,
  endDate: string,
  shape: PeriodShape
): { comparisonStart: string; comparisonEnd: string } | null {
  switch (shape) {
    // Same weekday, one week earlier — the whole point of the epic.
    case 'single-day': {
      const prior = shiftDateStr(startDate, -7);
      return { comparisonStart: prior, comparisonEnd: prior };
    }
    // Immediately preceding window of identical length.
    case 'seven-day':
    case 'thirty-day':
    case 'ninety-day': {
      const len = spanDays(startDate, endDate);
      return { comparisonStart: shiftDateStr(startDate, -len), comparisonEnd: shiftDateStr(endDate, -len) };
    }
    // Equivalent count of complete calendar months immediately preceding.
    case 'calendar-month':
    case 'three-calendar-months':
    case 'six-calendar-months': {
      const n = monthSpan(startDate, endDate);
      const [sy, sm] = parts(startDate);
      const [ey, em] = parts(endDate);
      const [csy, csm] = shiftMonth(sy, sm, -n);
      const [cey, cem] = shiftMonth(ey, em, -n);
      return { comparisonStart: firstOfMonth(csy, csm), comparisonEnd: lastOfMonth(cey, cem) };
    }
    case 'calendar-year': {
      const y = parts(startDate)[0] - 1;
      return { comparisonStart: `${y}-01-01`, comparisonEnd: `${y}-12-31` };
    }
    // Equal elapsed days on both sides: 1st of the prior month → same day of month.
    case 'month-to-date': {
      const [sy, sm] = parts(startDate);
      const [py, pm] = shiftMonth(sy, sm, -1);
      return { comparisonStart: firstOfMonth(py, pm), comparisonEnd: sameDayInShiftedMonth(endDate, -1) };
    }
    case 'year-to-date': {
      const y = parts(startDate)[0] - 1;
      return { comparisonStart: `${y}-01-01`, comparisonEnd: sameDayInShiftedYear(endDate, -1) };
    }
    default:
      return null;
  }
}

// ── Label formatting ─────────────────────────────────────────────────────────

/** "2 PM", "9 AM", "12 AM" — the truncation point printed in the comparison line. */
export function formatHourLabel(hour: number): string {
  const h = ((hour % 24) + 24) % 24;
  const suffix = h < 12 ? 'AM' : 'PM';
  return `${h % 12 === 0 ? 12 : h % 12} ${suffix}`;
}

/** "Jul 30" / "Jul 30, 2025" */
const fmtDay = (iso: string, withYear: boolean): string => {
  const [y, m, d] = parts(iso);
  return withYear ? `${MONTHS_SHORT[m - 1]} ${d}, ${y}` : `${MONTHS_SHORT[m - 1]} ${d}`;
};

/** "Thu, Jul 30" / "Thu, Jul 30, 2025" */
const fmtWeekday = (iso: string, withYear: boolean): string =>
  `${WEEKDAYS_SHORT[weekdayOf(iso)]}, ${fmtDay(iso, withYear)}`;

/**
 * Human label for a range of a given shape.
 * `withYear` is decided by the caller: the selection line shows the year only when the
 * range sits outside the current year, and the comparison line only when it falls in a
 * different calendar year than the selection.
 */
function formatRangeLabel(startDate: string, endDate: string, shape: PeriodShape, withYear: boolean): string {
  if (shape === 'single-day') return fmtWeekday(startDate, withYear);
  if (shape === 'calendar-month') {
    const [y, m] = parts(startDate);
    return `${MONTHS_LONG[m - 1]} ${y}`;
  }
  if (shape === 'calendar-year') return String(parts(startDate)[0]);
  if (shape === 'year-to-date') return String(parts(startDate)[0]);
  return `${fmtDay(startDate, withYear)} – ${fmtDay(endDate, withYear)}`;
}

// ── The resolver ─────────────────────────────────────────────────────────────

/**
 * Live store-timezone context. Split out so the resolver stays pure and testable.
 *
 * `todayISO` and `nowHour` both come from the SERVER's clock when one has arrived
 * (`currentTime` + `locationTimezone` on the comparison endpoints, mirrored into
 * `dateTimeUtils`), and both fall back to the device together when it has not or has gone
 * stale. Taking them from ONE reading is the point: the date text and the truncation hour
 * in "vs Fri, Jul 31 (to 7 AM)" describe the same moment, and the comparison window the
 * saga sends is derived from that same date, so the label can never name a period other
 * than the one actually requested.
 *
 * The device's `dateStrInZone(now, tz)` is only the store's day once `timeZoneCd` has
 * resolved and is right; the endpoints state it outright.
 *
 * `anchorISO` is deliberately NOT server-derived — the 5 AM cutoff stays exactly where
 * `getAnchorDate` puts it (see DateProdIssue.md); this module must never re-derive it.
 */
export function buildPeriodContext(timeZoneCd: string = getStoreTimeZone()): PeriodContext {
  const now = new Date();
  const server = getServerClock();
  return {
    todayISO: server ? server.dateISO : dateStrInZone(now, timeZoneCd),
    anchorISO: getAnchorDate(timeZoneCd),
    nowHour: server ? server.hour : hourInZone(now, timeZoneCd),
  };
}

/**
 * `'preceding'` basis (`DPFIX-13`): the equal-length window ending the day before the
 * selection starts. Defined for every bounded range, whatever its shape — the backend
 * that uses it computes a delta for a hand-picked 11-day range too.
 */
const precedingWindow = (startDate: string, endDate: string) => {
  const len = spanDays(startDate, endDate);
  return { comparisonStart: shiftDateStr(startDate, -len), comparisonEnd: shiftDateStr(endDate, -len) };
};

/**
 * Resolve an explicit range (already reduced from a token) into the full contract.
 * Exported for callers that hold dates rather than a token (period steppers).
 */
export function resolveRange(
  startDate: string,
  endDate: string,
  ctx: PeriodContext,
  token = '',
  basis: ComparisonBasis = 'shape'
): ResolvedPeriod {
  const shape = classifyShape(startDate, endDate, ctx);
  const cmp = basis === 'preceding'
    ? precedingWindow(startDate, endDate)
    : resolveComparisonRange(startDate, endDate, shape);
  const isToday = startDate === ctx.todayISO && endDate === ctx.todayISO;

  // Selection line: year shown only when the range leaves the current calendar year.
  const selYear = parts(startDate)[0];
  const selShowsYear = selYear !== parts(ctx.todayISO)[0] || parts(endDate)[0] !== parts(ctx.todayISO)[0];
  const selectionLabel = isToday ? 'Today' : formatRangeLabel(startDate, endDate, shape, selShowsYear);

  if (!cmp) {
    return {
      token, startDate, endDate, shape, isToday,
      selectionLabel,
      comparisonLabel: NO_COMPARISON_LABEL,
      comparisonAvailable: false,
      comparisonStart: '', comparisonEnd: '',
      hourTruncated: false, truncationHour: null,
    };
  }

  // ZAR-1662 — only a current-day selection is truncated; every other period compares in
  // full. Both windows cover midnight → the most recently completed clock hour, so an
  // 11 AM check-in is not measured against last week's full trading day.
  // Never on the 'preceding' basis: that backend compares today so far against the WHOLE
  // of yesterday, and a "(to 5 PM)" here would claim a cut-off it does not apply.
  const hourTruncated = isToday && basis === 'shape';
  const truncationHour = hourTruncated ? ctx.nowHour : null;

  // Comparison line: year shown only when the comparison leaves the selection's year.
  const cmpShowsYear =
    parts(cmp.comparisonStart)[0] !== selYear || parts(cmp.comparisonEnd)[0] !== parts(endDate)[0];
  // The comparison window is labelled in the same *kind* as the selection, and otherwise
  // falls back to an explicit range. That fallback is what makes a year-to-date selection
  // read "2026" over "vs Jan 1, 2025 – Jul 31, 2025" instead of collapsing to a bare
  // "2025", which would wrongly imply the whole prior year.
  // On the 'preceding' basis the window is a plain run of days whatever the selection
  // was — September's preceding 30 days are Aug 2 – Aug 31, not "August 2026" — so it is
  // only ever a weekday or an explicit range.
  const cmpLabelShape: PeriodShape =
    basis === 'preceding'
      ? (cmp.comparisonStart === cmp.comparisonEnd ? 'single-day' : 'unregistered')
      : shape === 'single-day' || shape === 'calendar-month' || shape === 'calendar-year'
        ? shape
        : 'unregistered';

  // ZAR-1662 — a current-day comparison names the point it is truncated at: "(to 7 AM)".
  // The hour rides in on `ctx.nowHour`, which `buildPeriodContext` sources from the
  // server's own clock when the API has supplied one, so the label tracks the data's
  // cutoff rather than the viewer's device clock.
  let comparisonLabel = `vs ${formatRangeLabel(cmp.comparisonStart, cmp.comparisonEnd, cmpLabelShape, cmpShowsYear)}`;
  if (hourTruncated && truncationHour != null) {
    comparisonLabel += ` (to ${formatHourLabel(truncationHour)})`;
  }

  return {
    token, startDate, endDate, shape, isToday,
    selectionLabel,
    comparisonLabel,
    comparisonAvailable: true,
    comparisonStart: cmp.comparisonStart,
    comparisonEnd: cmp.comparisonEnd,
    hourTruncated,
    truncationHour,
  };
}

/**
 * True for the one token that names no range: Guest Reviews' "All" (`#UD 12`).
 *
 * Kept as a predicate rather than folded into `resolveTokenRange` on purpose — that
 * function's contract is token -> {startDate, endDate}, and there is no honest pair of
 * dates to return here. It keeps answering `null`, which is also what makes
 * `useSharedReportDateFilter` treat "All" as un-carryable: a Guest-Reviews-only
 * selection should not follow the user onto Snapshot, which has no unbounded fetch.
 */
export const isUnboundedToken = (token: unknown): boolean => normalizeToken(token) === 'all';

/** The resolved period for an unbounded selection: no dates, no comparison, no steps. */
const unboundedPeriod = (token: string): ResolvedPeriod => ({
  token,
  startDate: '',
  endDate: '',
  shape: 'unbounded',
  isToday: false,
  selectionLabel: 'All time',
  comparisonLabel: NO_COMPARISON_LABEL,
  comparisonAvailable: false,
  comparisonStart: '',
  comparisonEnd: '',
  hourTruncated: false,
  truncationHour: null,
});

/**
 * Resolve a stored filter token into the full period + comparison contract.
 * Falls back to the store's current day when the token is unrecognised, so a stale or
 * corrupt persisted value can never leave a report without a range.
 */
export function resolvePeriod(
  token: string,
  ctx: PeriodContext,
  timeZoneCd?: string,
  basis: ComparisonBasis = 'shape'
): ResolvedPeriod {
  // "All" resolves to no range at all, so it cannot go through `resolveRange` — and it
  // must be caught BEFORE the today fallback below. Without this branch `resolveTokenRange`
  // returns null for it, the fallback substitutes today for both ends, and the control
  // reports the selection as "Today" with a week-over-week comparison line while the
  // screen is showing every review ever written (`#UD 12`).
  if (isUnboundedToken(token)) return unboundedPeriod(token);

  const range = resolveTokenRange(token, timeZoneCd ?? getStoreTimeZone());
  const start = range?.startDate ?? ctx.todayISO;
  const end = range?.endDate ?? ctx.todayISO;
  return resolveRange(start, end, ctx, token, basis);
}

/**
 * Comparison window for an already-resolved range, or null when the shape carries none.
 *
 * Used by the export builders (GLOBAL-DP-4): an exported file is read outside the
 * interface, where the date control that states the basis is not present, so the
 * comparison period has to travel with the file as a header row.
 */
export function comparisonRangeFor(
  startDate?: string,
  endDate?: string,
  timeZoneCd?: string
): { comparisonStart: string; comparisonEnd: string } | null {
  if (!startDate || !endDate) return null;
  const tz = timeZoneCd ?? getStoreTimeZone();
  const r = resolveRange(startDate, endDate, buildPeriodContext(tz));
  return r.comparisonAvailable
    ? { comparisonStart: r.comparisonStart, comparisonEnd: r.comparisonEnd }
    : null;
}

/**
 * The period an exported file is about — the label the date control shows, plus the
 * explicit calendar bounds behind it.
 *
 * An export is read away from the interface, so the file has to state its own period.
 * The label alone is not enough: "Today", "This Month" and "June 2026" all name a range
 * without stating where it starts and ends, and a reader in a spreadsheet three weeks
 * later cannot recover the dates from them. `startDate` / `endDate` are what make the
 * file self-describing, which is why they are non-optional here.
 */
export interface ExportPeriod {
  /**
   * The date control's selection line, **always carrying the year**.
   *
   * On screen the resolver drops the year while the range sits inside the current one —
   * "Aug 7 – Aug 13" — because the control is read in a context that supplies it. A file
   * is not: it is opened months later, alongside files from other years, by someone who
   * was not the person who exported it. So the export label pins the year even where the
   * control omits it.
   */
  label: string;
  /** ISO `YYYY-MM-DD`. */
  startDate: string;
  /** ISO `YYYY-MM-DD`. */
  endDate: string;
}

/**
 * Build the `ExportPeriod` for a report's current selection.
 *
 * Used by the Stamp-KPI exports on Snapshot and Sales (`DPFIX-05`). Snapshot's local label
 * helper ended in `if (filter === 'Custom') {…} return filter` — `'Custom'` being the token
 * this epic replaced with `custom:<from>:<to>` — so a hand-picked range matched no branch and
 * the RAW TOKEN was written into the export header: "A2b • 9 outlets •
 * custom:2013-08-01:2026-08-13". Deriving the label here is what keeps a file and the control
 * that produced it from wording the same period differently.
 *
 * It is the control's label with **one deliberate difference: the year is always shown.**
 * `resolveRange` omits it while the range stays inside the current calendar year, which is
 * right on screen and wrong in a file — "Aug 7 – Aug 13" names no year to whoever opens the
 * export next February. So the label is rebuilt through `formatRangeLabel` with `withYear`
 * forced, rather than taken from `selectionLabel` verbatim. `'Today'` is left as the word
 * "Today": it is the control's own name for the period, and the export's `From` / `To` rows
 * carry the date beside it.
 *
 * **Every export header in the app now comes through here** (DPFIX-07). Seven copies of this
 * used to exist — `getFilterLabel` ×2, `getExportDateRangeLabel` ×4, EOD's `formatRangeLabel`
 * — and they had already drifted apart: Sales' understood `custom:` and Snapshot's did not,
 * which is the whole of DPFIX-05. All seven are gone. The one survivor is Sales'
 * `getFilterLabel`, kept for four *on-screen* subtitle lines; it is not an export path, and
 * nothing new should join it.
 *
 * `range` (what the screen actually fetched) wins over `token` when both are given; they
 * agree in practice, since every screen derives its range from the token through
 * `resolveTokenRange`, but the fetched range is the one the numbers in the file came from.
 * Returns `null` only when neither yields a range, so a caller with nothing to state
 * stamps nothing rather than inventing today.
 */
export function exportPeriodFor(
  token?: string,
  range?: { startDate?: string; endDate?: string } | null,
  timeZoneCd?: string
): ExportPeriod | null {
  const tz = timeZoneCd ?? getStoreTimeZone();
  const explicit =
    range?.startDate && range?.endDate
      ? { startDate: range.startDate, endDate: range.endDate }
      : null;
  const resolved = explicit ?? (token ? resolveTokenRange(token, tz) : null);
  if (!resolved) return null;
  const r = resolveRange(resolved.startDate, resolved.endDate, buildPeriodContext(tz), token ?? '');
  const label = r.isToday
    ? r.selectionLabel
    : formatRangeLabel(r.startDate, r.endDate, r.shape, true);
  return { label, startDate: r.startDate, endDate: r.endDate };
}

/**
 * Comparison-window query params for the reports comparison API (ZAR-1661).
 *
 * The backend no longer derives the comparison window itself: the basis now follows the
 * SHAPE of the selection (same weekday a week back for a single day, the preceding
 * equal-length window for 7D/30D, the whole prior calendar month, …) and that shape is
 * only known here. So the FE computes the window and sends it down as `compStartDate` /
 * `compEndDate`.
 *
 * Both arguments are optional because the live "Today" call deliberately sends NO dates —
 * the ClickHouse endpoints read an absent range as "the current day" (see the `chDates`
 * branches in SnapshotScreen / SalesScreen). That call therefore resolves against the
 * store's current day and, per the agreed contract, carries `compStartDate` ALONE: its
 * comparison is a single day, so `compEndDate` would be redundant. Every historical call
 * carries BOTH, even when the comparison window is one day wide, so the contract stays
 * uniform for anything that is not the live day.
 *
 * Returns `{}` when the selection's shape carries no comparison at all — a hand-picked
 * 11-day range must not silently acquire one.
 */
export function comparisonQueryParams(
  startDate?: string,
  endDate?: string,
  timeZoneCd?: string
): { compStartDate?: string; compEndDate?: string } {
  const tz = timeZoneCd ?? getStoreTimeZone();
  const ctx = buildPeriodContext(tz);
  const start = startDate || ctx.todayISO;
  const end = endDate || startDate || ctx.todayISO;

  const period = resolveRange(start, end, ctx);
  if (!period.comparisonAvailable) return {};

  return period.isToday
    ? { compStartDate: period.comparisonStart }
    : { compStartDate: period.comparisonStart, compEndDate: period.comparisonEnd };
}

/**
 * Whether the selected period has a comparison at all — i.e. whether the KPI cards should
 * render a delta (ZAR-1661).
 *
 * Replaces the hardcoded token shortlists the screens used to gate deltas on
 * (`activeDate === 'Yesterday' || activeDate === '7D'`). Those lists predate the shape
 * registry and were both too narrow — "This month", "This year", "Last 30 days" and any
 * stepped or hand-picked range that happens to match a registered shape all have a
 * perfectly good comparison — and unmaintainable, since a stepper emits `custom:from:to`
 * tokens that no literal list can enumerate.
 *
 * Availability follows the SHAPE, so this is just `resolvePeriod(...).comparisonAvailable`.
 */
export function hasComparison(token: string, timeZoneCd?: string): boolean {
  const tz = timeZoneCd ?? getStoreTimeZone();
  return resolvePeriod(token, buildPeriodContext(tz), tz).comparisonAvailable;
}

/**
 * Tooltip text for a KPI delta (GLOBAL-DP-4).
 *
 * The per-card "vs Yesterday" label is gone — the basis is stated once in the date control —
 * but the basis must stay recoverable on an individual card, so each delta carries the
 * resolved comparison period as its tooltip.
 */
export function comparisonTooltip(token: string, timeZoneCd?: string): string {
  const tz = timeZoneCd ?? getStoreTimeZone();
  const period = resolvePeriod(token, buildPeriodContext(tz), tz);
  if (!period.comparisonAvailable) return NO_COMPARISON_LABEL;
  return `Compared with ${period.comparisonLabel.replace(/^vs /, '')}`;
}

// ── Period steppers (ZAR-1640) ───────────────────────────────────────────────

/**
 * Move a selection one whole period backwards/forwards.
 *
 * Calendar-aligned shapes step by calendar units, so stepping back from a 31-day July
 * lands on the whole of June (28/29/30/31 days), never on a truncated 31-day window.
 * Everything else steps by its own exact day count. Relative presets ("Today",
 * "This month") therefore become explicit ranges the moment they are stepped, which is
 * what the spec asks for — "This month" stepped back reads "June 2026", not "This month".
 *
 * Always returns a `custom:from:to` token, or null when the step is not permitted.
 */
export function stepPeriod(
  period: Pick<ResolvedPeriod, 'startDate' | 'endDate' | 'shape'>,
  direction: -1 | 1,
  ctx: PeriodContext
): string | null {
  const { startDate, endDate, shape } = period;
  // An unbounded selection has no length to step by, in either direction — both
  // steppers are disabled rather than silently converting "All" into a finite window.
  if (shape === 'unbounded') return null;
  let nextStart: string;
  let nextEnd: string;

  switch (shape) {
    case 'calendar-month':
    case 'three-calendar-months':
    case 'six-calendar-months':
    case 'month-to-date': {
      // A month-to-date step resolves to a WHOLE calendar month — a partial July
      // stepped back is all of June, not "Jun 1 – Jun 8".
      const n = shape === 'month-to-date' ? 1 : monthSpan(startDate, endDate);
      const [sy, sm] = parts(startDate);
      const [ey, em] = shape === 'month-to-date' ? [sy, sm] : parts(endDate);
      const [nsy, nsm] = shiftMonth(sy, sm, direction * n);
      const [ney, nem] = shiftMonth(ey, em, direction * n);
      nextStart = firstOfMonth(nsy, nsm);
      const [todayY, todayM] = parts(ctx.todayISO);
      // Stepping (forward, from a completed prior month) back into the CURRENT,
      // still-running month must reconstruct the month-to-date shape — ending today —
      // not the full last-of-month date. Otherwise that date is in the future and trips
      // the never-step-into-the-future guard below, silently freezing the stepper one
      // step after going back (e.g. "This month" -> back a month -> forward is a no-op).
      nextEnd = (ney === todayY && nem === todayM) ? ctx.todayISO : lastOfMonth(ney, nem);
      break;
    }
    case 'calendar-year':
    case 'year-to-date': {
      const y = parts(startDate)[0] + direction;
      nextStart = `${y}-01-01`;
      const todayY = parts(ctx.todayISO)[0];
      // Same reconstruction as the month case above, for "This year".
      nextEnd = y === todayY ? ctx.todayISO : `${y}-12-31`;
      break;
    }
    default: {
      const len = spanDays(startDate, endDate);
      nextStart = shiftDateStr(startDate, direction * len);
      nextEnd = shiftDateStr(endDate, direction * len);
      break;
    }
  }

  // Never step into the future, and never silently clamp to a shorter period — the
  // control disables the forward stepper instead, so the user is never shown an
  // unexplained truncated range.
  if (nextEnd > ctx.todayISO) return null;
  return customToken(nextStart, nextEnd);
}

/** True when the forward stepper should be enabled for this selection. */
export const canStepForward = (
  period: Pick<ResolvedPeriod, 'startDate' | 'endDate' | 'shape'>,
  ctx: PeriodContext
): boolean => stepPeriod(period, 1, ctx) !== null;
